"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import studentImage from "../resources/9197801.jpg"
import teacherImage from "../resources/9197810.jpg"
import { postAws } from "../postAws"
import { ArrowRight, ArrowLeft, Loader, CheckCircle, X, Clock } from "lucide-react"
import { Link } from "react-router-dom"

// A simple list of country codes. In a real application, you'd want a more comprehensive list.
const countryCodes = [
  { code: "+1", country: "USA" },
  { code: "+44", country: "UK" },
  { code: "+91", country: "India" },
  { code: "+81", country: "Japan" },
  // Add more country codes as needed
]

function OtpModal({ email, onVerify, onClose, onResend }) {
  const [otp, setOtp] = useState("")
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes in seconds
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [otpError, setOtpError] = useState("")

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer)
          setIsTimedOut(true)
          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsVerifying(true)
    setOtpError("")
    try {
      await onVerify(otp)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setOtpError("Invalid OTP, please try again")
      } else {
        setOtpError("An error occurred, please try again")
      }
    }
    setIsVerifying(false)
  }

  const handleResend = async () => {
    setIsResending(true)
    await onResend()
    setIsResending(false)
    setTimeLeft(300)
    setIsTimedOut(false)
    setOtpError("")
  }

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl relative w-full max-w-md">
        <button onClick={onClose} className="absolute top-2 right-2">
          <X className="h-6 w-6 text-gray-500" />
        </button>
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Enter 6-digit OTP</h2>
        <p className="mb-4 text-sm sm:text-base">OTP has been sent to your registered email: {email}</p>
        {!isTimedOut && <p className="mb-4 text-sm sm:text-base">Valid for 5 minutes</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter OTP"
            required
            disabled={isTimedOut}
          />
          {otpError && <p className="mt-1 text-sm text-red-600">{otpError}</p>}
          {isTimedOut ? (
            <button
              type="button"
              onClick={handleResend}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
              disabled={isResending}
            >
              {isResending ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Resending...
                </>
              ) : (
                "Resend OTP"
              )}
            </button>
          ) : (
            <button
              type="submit"
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
              disabled={isVerifying}
            >
              {isVerifying ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Verifying...
                </>
              ) : (
                "Verify OTP"
              )}
            </button>
          )}
        </form>
        <div className="mt-4 flex items-center justify-end">
          <Clock className="h-5 w-5 text-gray-500 mr-2" />
          <span className={`text-gray-500 ${isTimedOut ? "text-red-500" : ""}`}>
            {isTimedOut ? "OTP Timed Out" : formatTime(timeLeft)}
          </span>
        </div>
      </div>
    </div>
  )
}

function SuccessModal() {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl text-center w-full max-w-md">
        <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl sm:text-2xl font-bold mb-4">OTP Verified</h2>
        <p className="mb-4 text-sm sm:text-base">User account created successfully!</p>
        <p className="text-gray-500 text-sm sm:text-base">Redirecting to login page...</p>
      </div>
    </div>
  )
}

export default function Signup() {
  const [isStudent, setIsStudent] = useState(false)
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    countryCode: "+1",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  })
  const [passwordError, setPasswordError] = useState("")
  const [error, setError] = useState("")
  const [emailError, setEmailError] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const navigate = useNavigate()

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))

    if (name === "email") {
      if (!value.includes("@")) {
        setEmailError("Invalid email address")
      } else {
        setEmailError("")
      }
    }

    if (name === "phoneNumber") {
      if (!/^\d*$/.test(value)) {
        setPhoneError("Phone number can only contain digits")
      } else {
        setPhoneError("")
      }
    }

    if (name === "confirmPassword") {
      setConfirmPasswordTouched(true)
      if (value !== formData.password) {
        setPasswordError("Passwords do not match")
      } else {
        setPasswordError("")
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match")
      return
    }
    if (emailError || phoneError) {
      return
    }
    await sendSignUpData()
  }

  const sendSignUpData = async () => {
    setIsSubmitting(true)
    try {
      const signupData = {
        body: {
          teacherName: formData.fullName,
          teacherEmail: formData.email,
          password: formData.password,
          countryCode: formData.countryCode,
          phoneNumber: `${formData.countryCode}${formData.phoneNumber}`,
        },
        request: "signup-Teacher",
      }
      const response = await postAws("https://api-dev.aurait.ai/auth/teacher-auth", "POST", signupData)

      if (response.statusCode === 200) {
        setShowOtpModal(true)
      } else {
        console.error(response.body.error)
        setError(response.body.error)
      }
    } catch (error) {
      console.error("Error during sign-up:", error)
      // Handle error (e.g., show error message to user)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOtpVerification = async (otp) => {
    try {
      const signupData = {
        body: {
          teacherEmail: formData.email,
          otp: otp,
        },
        request: "verify-otp-Teacher",
      }
      const response = await postAws("https://api-dev.aurait.ai/auth/teacher-auth", "POST", signupData)

      if (response.statusCode === 201) {
        setShowOtpModal(false)
        setShowSuccessModal(true)
        setTimeout(() => {
          setShowSuccessModal(false)
          navigate("/login")
        }, 10000)
      } else {
        throw new Error(response.body.message || "OTP verification failed")
      }
    } catch (error) {
      console.error("Error during OTP verification:", error)
      throw error
    }
  }

  const handleResendOtp = async () => {
    await sendSignUpData()
  }

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu)
  }

  return (
    <div className="flex flex-col items-center w-full min-h-screen justify-between">
      {/* Header */}
      <header className="bg-white shadow-md rounded-md top-0 z-50 w-full py-2 opacity-95 flex justify-center">
        <div className="w-full px-4 sm:w-11/12 md:w-4/5 lg:w-3/4 flex items-center justify-between">
          <div className="logo-name">
            <Link to={"/"} className="flex items-center gap-2 py-2">
              <div>
                <h1 className="flex text-2xl sm:text-3xl font-semibold">
                  <span>पढ़.</span>
                  <span className="gradient-text font-semibold">AI</span>
                </h1>
              </div>
            </Link>
          </div>
          <div className="hidden md:flex gap-4 items-center">
            <Link
              to="/signup"
              className="border-gray-600 border-2 rounded-xl bg-white text-gray-800 font-semibold text-lg py-1 px-6 rounded"
            >
              Sign up
            </Link>
            <Link to="/login" className="gradient-border">
              <button className="bg-white text-gray-800 font-semibold text-lg py-1 px-6 rounded focus:outline-none focus:shadow-outline rounded-xl">
                Login
              </button>
            </Link>
          </div>
          <button className="md:hidden flex items-center p-2" onClick={toggleMobileMenu} aria-label="Toggle menu">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={showMobileMenu ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="md:hidden w-full bg-white shadow-md py-2 px-4 absolute top-14 z-40">
          <div className="flex flex-col space-y-3">
            <Link
              to="/signup"
              className="border-gray-600 border-2 rounded-xl bg-white text-gray-800 font-semibold text-lg py-1 px-6 text-center"
            >
              Sign up
            </Link>
            <Link to="/login" className="gradient-border">
              <button className="w-full bg-white text-gray-800 font-semibold text-lg py-1 px-6 rounded focus:outline-none focus:shadow-outline rounded-xl">
                Login
              </button>
            </Link>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center p-4 flex-grow w-full">
        <div className="w-full max-w-4xl">
          {/* Toggle Button */}
          <div className={`w-full mb-4 flex ${isStudent ? "justify-start" : "justify-end"}`}>
            <button className="bg-indigo-600 text-white px-4 sm:px-6 py-2 rounded-md shadow-md hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base">
              {isStudent ? (
                <>
                  <ArrowLeft className="w-4 h-4" />
                  Sign up as Teacher
                </>
              ) : (
                <a className="flex items-center" href="https://https://padhai-student.aurait.ai/signup/">
                  Sign up as Student
                  <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </button>
          </div>
          {/* Signup Card */}
          <div
            className={`w-full bg-white rounded-lg shadow-xl overflow-hidden ${isStudent ? "student-mode" : "teacher-mode"}`}
          >
            <div className="flex flex-col md:flex-row">
              <div className={`w-full md:w-1/2 p-4 sm:p-6 md:p-8 ${isStudent ? "order-last" : "order-first"}`}>
                <h2 className="text-2xl font-bold mb-4 text-center">
                  {isStudent ? "Student Sign Up" : "Teacher Sign Up"}
                </h2>
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                    {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.code} ({country.country})
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="flex-1 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    {phoneError && <p className="mt-1 text-sm text-red-600">{phoneError}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                    {confirmPasswordTouched && passwordError && (
                      <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="animate-spin h-5 w-5 mr-2" />
                        Signing Up...
                      </>
                    ) : (
                      "Sign Up"
                    )}
                  </button>
                </form>
                {error && <span className="text-red-600 text-sm mt-2 block">{error}</span>}
              </div>
              <div className={`w-full md:w-1/2 ${isStudent ? "order-first" : "order-last"}`}>
                <div className="h-48 sm:h-64 md:h-full">
                  <img
                    src={isStudent ? studentImage : teacherImage}
                    alt={isStudent ? "Student" : "Teacher"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white w-full py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex gap-2 items-center">
              <span className="text-xl sm:text-2xl font-bold">Padhai </span>
              <span className="text-sm sm:text-lg font-medium">An AURA-AI Initiative</span>
            </div>
            <div className="text-center text-gray-400 text-sm">© 2025 Padhai. All rights reserved.</div>
            <nav>
              <ul className="flex space-x-4">
                <li>
                  <a href="#" className="hover:text-indigo-400 text-sm">
                    About
                  </a>
                </li>
                <li>
                  <Link to="/bookAdemo" className="hover:text-indigo-400 text-sm">
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </footer>

      {showOtpModal && (
        <OtpModal
          email={formData.email}
          onVerify={handleOtpVerification}
          onClose={() => setShowOtpModal(false)}
          onResend={handleResendOtp}
        />
      )}
      {showSuccessModal && <SuccessModal />}
    </div>
  )
}

